import { formatSecondsAsTimecode } from '@kiao-voice/core'
import { MEETING_SPEAKERS, WINDOW_SECS, type MeetingCue } from './meeting-demo-cues'

/**
 * 會議示範的波形、講者段落塊與刻度尺，全部是純函式且**確定性**：同樣的輸入永遠
 * 畫出同一張圖，重新整理長得一樣。演算法照設計稿 `site-transcription.html` 的
 * `<script>` 逐行翻過來，不是另外想一個看起來差不多的。
 *
 * 為什麼要確定性：這張波形是產品的展示品，不是裝飾。每次重畫都不一樣的話，
 * 視覺回歸測試永遠紅，而讀者也會以為那是真的音訊。
 */

/** 設計稿的決定式「亂數」。同一個 n 永遠回同一個 0–1 的值。 */
export function hash(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

export interface WaveCell {
  t0: number
  t1: number
  /** 標點格：後半段是停頓，振幅掉到底噪。 */
  pause: boolean
  peak: number
  /** 來源句的 index，讓同一句的顫動有一致的相位。 */
  sid: number
}

const PAUSE_CHARS = /[，。？！、；：,.?!]/

/**
 * 每句依字數切成等寬的格，一格一個音節包絡。空白不算字——它不發音，
 * 算進去會讓中英夾雜的句子憑空多出幾格靜音。
 */
export function buildWaveCells(cues: MeetingCue[]): WaveCell[] {
  const cells: WaveCell[] = []
  cues.forEach((cue, cueIndex) => {
    const chars = cue.text.replace(/\s+/g, '').split('')
    if (chars.length === 0) return
    const step = (cue.end - cue.start) / chars.length
    chars.forEach((char, i) => {
      cells.push({
        t0: cue.start + i * step,
        t1: cue.start + (i + 1) * step,
        pause: PAUSE_CHARS.test(char),
        peak: 0.42 + 0.58 * hash(cueIndex * 131 + i),
        sid: cueIndex,
      })
    })
  })
  return cells
}

/**
 * 某個時間點的振幅，0.018–1。句與句之間是底噪，句內是「快起慢收」的包絡。
 */
export function waveAmplitudeAt(t: number, cells: WaveCell[]): number {
  const cell = cells.find((candidate) => t < candidate.t1)
  let amp: number
  if (!cell || t < cell.t0) {
    amp = 0.015 + 0.02 * hash(Math.floor(t * 140))
  } else {
    const u = (t - cell.t0) / Math.max(1e-4, cell.t1 - cell.t0)
    const voiced = cell.pause ? 0.44 : 0.92
    if (u > voiced) {
      amp = 0.02 + 0.015 * hash(Math.floor(t * 140) + 7)
    } else {
      const v = u / voiced
      const env = v < 0.16 ? v / 0.16 : Math.pow(1 - (v - 0.16) / 0.84, 0.62)
      amp = env * cell.peak * (0.7 + 0.3 * Math.abs(Math.sin(t * 128.7 + cell.sid)))
    }
  }
  return Math.max(0.018, Math.min(1, amp))
}

export interface WaveBar {
  x: number
  /** 高度（px），至少 1，否則靜音處整條線會消失。 */
  h: number
}

export interface WaveBarOptions {
  bar?: number
  pitch?: number
  window?: number
  /** 可用高度（bar 的最大值）。 */
  span: number
}

/** 一整條波形的長條幾何。畫布只負責把它們塗出來。 */
export function waveBars(
  width: number,
  cells: WaveCell[],
  { bar = 2, pitch = 3, window = WINDOW_SECS, span }: WaveBarOptions
): WaveBar[] {
  if (width < 1) return []
  const count = Math.floor(width / pitch)
  const bars: WaveBar[] = []
  for (let i = 0; i < count; i += 1) {
    const x = i * pitch
    const t = ((x + bar / 2) / width) * window
    bars.push({ x, h: Math.max(1, waveAmplitudeAt(t, cells) * span) })
  }
  return bars
}

export interface SpeakerBlock {
  index: number
  speaker: 1 | 2
  name: string
  leftPct: number
  widthPct: number
}

/** 時間軸上的講者段落塊，一句一塊。 */
export function speakerBlocks(cues: MeetingCue[], window = WINDOW_SECS): SpeakerBlock[] {
  return cues.map((cue, index) => ({
    index,
    speaker: cue.speaker,
    name: MEETING_SPEAKERS[cue.speaker],
    leftPct: (cue.start / window) * 100,
    widthPct: ((cue.end - cue.start) / window) * 100,
  }))
}

export interface RulerTick {
  leftPct: number
  label: string
}

/** 每 10 秒一格的刻度。 */
export function rulerTicks(window = WINDOW_SECS, stepSecs = 10): RulerTick[] {
  const ticks: RulerTick[] = []
  for (let t = 0; t < window; t += stepSecs) {
    ticks.push({ leftPct: (t / window) * 100, label: formatClock(t) })
  }
  return ticks
}

/**
 * 目前播到第幾句；不在任何一句範圍內時回 -1（句與句之間的空檔）。
 *
 * 由後往前找：句子相鄰時前一句的 end 等於下一句的 start，由前往後找會停在
 * 已經結束的那一句上。
 */
export function activeCueIndexAt(t: number, cues: MeetingCue[]): number {
  for (let index = cues.length - 1; index >= 0; index -= 1) {
    const cue = cues[index]
    if (t >= cue.start && t < cue.end) return index
  }
  return -1
}

/** 播放列的時鐘：`0:04`，分鐘不補零（與逐字稿編輯器一致）。 */
export function formatClock(secs: number): string {
  return formatSecondsAsTimecode(secs, { padMinutes: false })
}

/**
 * 逐句列的時間碼：`00:04.12`。百分之一秒是刻意保留的——它是 cue 的邊界，
 * 讀者拿它跟音檔對齊，捨去就對不準了。
 */
export function formatCueTimecode(secs: number): string {
  // 先量化到百分之一秒再拆，否則 8.999 會印成 `00:08.100`——秒數與小數各自
  // 進位的經典裂縫。
  const totalCentis = Math.max(0, Math.round(Number.isFinite(secs) ? secs * 100 : 0))
  const whole = Math.floor(totalCentis / 100)
  const centis = totalCentis % 100
  return `${formatSecondsAsTimecode(whole)}.${centis.toString().padStart(2, '0')}`
}
